import { useRoute } from "vue-router"
import { ref, watch, onMounted } from "vue"
import { main_menu } from "~/common/main_menu"
import { useI18n } from "vue-i18n"

export function useAutoBreadcrumbs() {
    const route = useRoute()
    const breadCrumbs = ref<any[]>([])
    const i18n = useI18n()

    const customBreadCrumbs = [
        {
            key: "create",
            labelKey: i18n.t("breadcrumbs.create"),
            icon: "add",
        },
        {
            key: "edit",
            labelKey: i18n.t("breadcrumbs.edit"),
            icon: "edit",
        },
        {
            key: "show",
            labelKey: i18n.t("breadcrumbs.show"),
            icon: "visibility",
        },
        {
            key: "managers",
            labelKey: i18n.t("breadcrumbs.managers"),
            icon: "person_pin",
        },
        {
            key: "settings",
            labelKey: i18n.t("breadcrumbs.settings"),
            icon: "settings_account_box",
        },
        {
            key: "stripe-proxy",
            labelKey: i18n.t("breadcrumbs.stripe-proxy"),
            icon: "credit_card",
        },
    ]

    // Función para encontrar el elemento de menú correspondiente a una ruta
    function findMenuItemByPath(
        path: string,
        menu = main_menu
    ): any | undefined {
        // Búsqueda exacta en el menú
        for (const item of menu) {
            if (item.route === path) return item
            if (item.children) {
                const found = findMenuItemByPath(path, item.children)
                if (found) return found
            }
        }

        return undefined
    }

    // Función para obtener el título de un segmento de ID
    function getIdSegmentTitle(id: string, parentPath: string): string {
        // Aquí podrías implementar lógica para obtener el nombre real del recurso si es necesario
        return id
    }

    // Agregar esta nueva función para buscar elementos con breadCrumbRoute
    function findBreadCrumbRouteParent(
        path: string,
        menu = main_menu
    ): any | undefined {
        let bestMatch = undefined
        let maxLength = 0

        function search(items: any[]) {
            for (const item of items) {
                if (
                    item.breadCrumbRoute &&
                    item.children?.some((i: any) =>
                        i.route?.toString().startsWith(path)
                    )
                ) {
                    bestMatch = item
                    maxLength = 9999
                }

                // Si la ruta actual incluye la ruta del ítem y el ítem tiene breadCrumbRoute
                if (
                    item.route &&
                    path.startsWith(item.route) &&
                    item.route.length > maxLength
                ) {
                    bestMatch = item
                    maxLength = item.route.length
                }

                // Buscar en los hijos también
                if (item.children) {
                    search(item.children)
                }
            }
        }

        search(menu)
        return bestMatch
    }

    // Función auxiliar para procesar segmentos personalizados
    function processCustomSegment(segment: string, accumulatedPath: string) {
        const customItem = customBreadCrumbs.find((item) => item.key === segment)
        if (customItem) {
            return {
                ...customItem,
                to: accumulatedPath,
            }
        }
        return null
    }

    // Función auxiliar para procesar segmentos de ID
    function processIdSegment(segment: string, accumulatedPath: string, prevAccumulatedPath: string) {
        const isNumericId = /^\d+$/.test(segment)
        if (isNumericId || segment.includes("-")) {
            return {
                labelKey: getIdSegmentTitle(segment, prevAccumulatedPath),
                to: accumulatedPath,
                key: segment,
                icon: "tag",
            }
        }
        return null
    }

    // Función auxiliar para procesar segmentos del menú
    function processMenuSegment(accumulatedPath: string) {
        const menuItem = findMenuItemByPath(accumulatedPath)
        if (menuItem) {
            return {
                labelKey: i18n.t(menuItem.title),
                to: accumulatedPath,
                icon: menuItem.icon,
                key: menuItem.title,
            }
        }
        return null
    }

    // Función auxiliar para procesar segmentos con breadCrumbRoute
    function processBreadCrumbRouteSegment(accumulatedPath: string) {
        const breadCrumbParent = findBreadCrumbRouteParent(accumulatedPath)
        if (breadCrumbParent) {
            return {
                labelKey: i18n.t(breadCrumbParent.title),
                to: accumulatedPath,
                icon: breadCrumbParent.icon,
                key: breadCrumbParent.title,
            }
        }
        return null
    }

    // Función auxiliar para procesar segmentos por defecto
    function processDefaultSegment(segment: string, accumulatedPath: string) {
        return {
            labelKey: i18n.t("breadcrumbs." + segment) || segment,
            to: accumulatedPath,
            key: segment,
            icon: "folder",
        }
    }

    // Función principal para generar las migas de pan
    const generateBreadCrumbs = () => {
        const newBreadCrumbs = []
        const pathSegments = route.path.split("/").filter(Boolean)
        let accumulatedPath = ""

        // Agregar breadcrumb de inicio
        newBreadCrumbs.push({
            labelKey: i18n.t("nav.manage"),
            to: null,
            icon: "work",
        })

        // Recorrer los segmentos excepto el primero (admin)
        for (let i = 1; i < pathSegments.length; i++) {
            const segment = pathSegments[i]
            const prevAccumulatedPath = accumulatedPath
            accumulatedPath += "/" + segment

            // Si es el primer segmento después de admin, la ruta es /admin/segmento
            if (i === 1) {
                accumulatedPath = "/" + pathSegments[0] + "/" + segment
            }

            // Procesar el segmento usando las funciones auxiliares
            let breadcrumb = processCustomSegment(segment, accumulatedPath)
            
            if (!breadcrumb) {
                breadcrumb = processIdSegment(segment, accumulatedPath, prevAccumulatedPath)
            }
            
            if (!breadcrumb) {
                breadcrumb = processMenuSegment(accumulatedPath)
            }
            
            if (!breadcrumb) {
                breadcrumb = processBreadCrumbRouteSegment(accumulatedPath)
            }
            
            if (!breadcrumb) {
                breadcrumb = processDefaultSegment(segment, accumulatedPath)
            }

            newBreadCrumbs.push(breadcrumb)
        }

        return newBreadCrumbs
    }

    onMounted(() => (breadCrumbs.value = generateBreadCrumbs()))

    watch(
        () => route.path,
        () => {
            breadCrumbs.value = generateBreadCrumbs()
        }
    )

    return { breadCrumbs }
}
